import { Injectable, inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import {
  INafathRequest,
  INafathResponse,
} from '@common/interfaces/nafath/nafath.interface';
import { API_ENDPOINTS } from '@common/utils/api-endpoints.const';
import { AppStorage, getCookieOptions, getCookieValue } from '@ui-lib';

/**
 * Nafath Service
 * Handles Nafath verification API calls and cookie storage
 */
@Injectable({
  providedIn: 'root',
})
export class NafathService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieService = inject(CookieService);
  // ngx-cookie-service can't read cookies during SSR, so we read from the raw request instead.
  private readonly request = inject(REQUEST, { optional: true });
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly COOKIE_OPTIONS = getCookieOptions(this.platformId);

  /**
   * Initiate Nafath verification
   * POST /api/v1/nafath
   * Automatically stores OTP from response on success
   */
  initiate(request: INafathRequest): Observable<INafathResponse> {
    return this.http.post<INafathResponse>(API_ENDPOINTS.NAFATH, request).pipe(
      tap(response => {
        this.setNafathOtp(response.nafathRequest.randomNumber);
      })
    );
  }

  /**
   * Update Nafath verification
   * PUT /api/v1/nafath
   * Automatically stores OTP from response on success
   */
  update(request: INafathRequest): Observable<INafathResponse> {
    return this.http.put<INafathResponse>(API_ENDPOINTS.NAFATH, request).pipe(
      tap(response => {
        this.setNafathOtp(response.nafathRequest.randomNumber);
      })
    );
  }

  /**
   * Check Nafath verification status
   * GET /api/v1/nafath/check-status
   */
  checkStatus(): Observable<INafathResponse> {
    return this.http.get<INafathResponse>(API_ENDPOINTS.NAFATH_CHECK_STATUS);
  }

  /**
   * Set Nafath OTP in cookie (SSR-safe)
   */
  setNafathOtp(otp: string): void {
    this.cookieService.set(AppStorage.nafathOtp, otp, this.COOKIE_OPTIONS);
  }

  /**
   * Get Nafath OTP from cookie (SSR-safe)
   */
  getNafathOtp(): string | null {
    return getCookieValue(
      AppStorage.nafathOtp,
      this.isBrowser,
      this.cookieService,
      this.request
    );
  }

  /**
   * Clear Nafath OTP from cookie
   */
  clearNafathOtp(): void {
    this.cookieService.delete(AppStorage.nafathOtp, '/');
  }
}
